import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { NV_PATHS, NvAccessDialog, NvAlertDialog, NvButton } from "@/components/navigator";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { accessDenialOf } from "@/lib/access";
import { getApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { downloadDefinition, getAgentDefinition, type AgentDefinition, type DefinitionFormat } from "@/services/navigator/agent-detail-service";

import { NvLucideCloseIcon } from "./detail-icons";

// Agent 정의서 뷰어 (Figma 203:5905, IA 117 · 120): 헤더가 있는 전체 화면 — 문서 목차 · 페이지 이동 · 확대/축소 · 형식 선택 · 내려받기 · 닫기.
// 1차는 등록 정보로 만든 정의서라 목차 항목 하나를 한 페이지로 보여줘요 (가정 A45).

const FORMATS: { value: DefinitionFormat; label: string }[] = [
  { value: "pdf", label: "PDF" },
  { value: "html", label: "HTML" },
  { value: "md", label: "Markdown" },
];
const ZOOM_STEPS = [50, 75, 90, 100, 110, 125, 150, 175, 200];

// 서버가 이스케이프해 만든 정의서 HTML을 Figma 정의서 페이지 글자 · 표 모양으로
const DOC_CLASS = cn(
  "text-nv-text-primary",
  "[&_.eyebrow]:font-num [&_.eyebrow]:text-[11px] [&_.eyebrow]:font-bold [&_.eyebrow]:leading-[13.3px] [&_.eyebrow]:text-nv-brand-text",
  "[&_h1]:mt-[14px] [&_h1]:text-[20px] [&_h1]:font-bold [&_h1]:leading-6",
  "[&_.meta]:mt-1 [&_.meta]:text-[12px] [&_.meta]:leading-4 [&_.meta]:text-nv-text-tertiary",
  "[&_h2]:mb-[14px] [&_h2]:mt-[14px] [&_h2]:text-[15px] [&_h2]:font-bold [&_h2]:leading-[18px]",
  "[&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-[13px] [&_h3]:font-bold [&_h3]:leading-4",
  "[&_p]:text-[13px] [&_p]:leading-[22.1px] [&_p]:text-nv-text-secondary",
  "[&_li]:text-[13px] [&_li]:leading-[22.1px] [&_li]:text-nv-text-secondary [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5",
  "[&_table]:w-full [&_table]:border-separate [&_table]:border-spacing-0 [&_table]:overflow-hidden [&_table]:rounded-nv-card [&_table]:border [&_table]:border-nv-border",
  "[&_th]:w-[160px] [&_th]:border-b [&_th]:border-nv-border [&_th]:bg-nv-bg-page [&_th]:px-[14px] [&_th]:py-3 [&_th]:text-left [&_th]:align-top [&_th]:text-[12px] [&_th]:font-bold [&_th]:leading-[14.4px] [&_th]:text-nv-text-secondary",
  "[&_td]:border-b [&_td]:border-nv-border [&_td]:px-[14px] [&_td]:py-3 [&_td]:align-top [&_td]:text-[13px] [&_td]:leading-[15.6px]",
  "[&_tr:last-child_td]:border-b-0 [&_tr:last-child_th]:border-b-0 [&_.empty]:text-nv-text-tertiary",
);

function splitPages(def: AgentDefinition) {
  const doc = new DOMParser().parseFromString(def.html, "text/html");
  const head = Array.from(doc.body.children)
    .filter((el) => el.tagName !== "SECTION")
    .map((el) => el.outerHTML)
    .join("");
  const sections = Array.from(doc.querySelectorAll("section")).map((el) => ({ id: el.id, html: el.outerHTML }));
  return sections.map((section, i) => (i === 0 ? { ...section, html: head + section.html } : section));
}

export function NavigatorAgentDefinitionPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const gate = useAccessGate();
  const [failure, setFailure] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [format, setFormat] = useState<DefinitionFormat>("pdf");
  const [busy, setBusy] = useState(false);

  // A late response of another agent's definition is ignored; the page index starts over for each agent
  const { data: def, error } = useRequest((signal) => getAgentDefinition(id, undefined, signal), [id]);
  const denial = accessDenialOf(error);
  useEffect(() => {
    if (error && !denial) setFailure(getApiError(error)?.message ?? "정의서를 불러오지 못했어요.");
  }, [error, denial]);
  useEffect(() => {
    setPage(0);
    window.scrollTo(0, 0);
  }, [id]);

  const pages = useMemo(() => (def ? splitPages(def) : []), [def]);
  const detailPath = `/agents/${id}`;
  const close = () => navigate(detailPath);
  const zoomBy = (dir: 1 | -1) => {
    const index = ZOOM_STEPS.indexOf(zoom);
    setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, index + dir))]);
  };
  const download = async () => {
    setBusy(true);
    try {
      await downloadDefinition(id, format);
    } catch {
      setFailure("정의서를 내려받지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  };

  const dialogs = (
    <>
      <NvAccessDialog {...gate.dialogProps} open={denial !== null} reason={denial} onOpenChange={(open) => !open && navigate(NV_PATHS.agentFinder, { replace: true })} />
      <NvAlertDialog
        open={failure !== null}
        onOpenChange={(open) => !open && setFailure(null)}
        message={failure ?? ""}
        actions={<NvButton onClick={() => (def ? setFailure(null) : close())}>확인</NvButton>}
      />
    </>
  );

  if (!def) return <div className="flex flex-1 items-center justify-center py-40">{dialogs}</div>;

  const sep = <span className="px-[6px]">/</span>;
  return (
    <div className="flex flex-1 flex-col">
      <header className="border-b border-nv-border bg-nv-surface">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-4 px-5 py-5 xl:px-[120px]">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <nav aria-label="경로" className="text-[12px] leading-[14.4px] text-nv-text-tertiary">
              <Link to={NV_PATHS.agentFinder} className="hover:text-nv-text-primary">
                Agent Finder
              </Link>
              {def.breadcrumb_industry && (
                <>
                  {sep}
                  <span>{def.breadcrumb_industry}</span>
                </>
              )}
              {sep}
              <Link to={detailPath} className="hover:text-nv-text-primary">
                {def.agent_name}
              </Link>
              {sep}
              <span>Agent 정의서</span>
            </nav>
            <div className="flex flex-wrap items-center gap-[10px]">
              <h1 className="text-[20px] font-bold leading-6 text-nv-text-primary">{def.title}</h1>
              <span className="inline-flex h-[21px] items-center rounded-nv-pill bg-nv-bg-page px-[10px] text-[11px] font-medium leading-[13px] text-nv-text-secondary">
                {[def.version, def.updated_at].filter(Boolean).join(" · ")}
              </span>
            </div>
          </div>
          <div role="radiogroup" aria-label="내려받을 형식" className="flex gap-[2px] rounded-nv-pill bg-nv-bg-page p-[3px]">
            {FORMATS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={format === f.value}
                onClick={() => setFormat(f.value)}
                className={cn("h-[21px] rounded-nv-pill px-[10px] text-[11px] leading-[13px]", format === f.value ? "bg-nv-surface font-bold text-nv-text-primary" : "font-medium text-nv-text-secondary")}
              >
                {f.label}
              </button>
            ))}
          </div>
          <NvButton size="sm" onClick={download} disabled={busy} className="h-[38px] text-[14px] leading-[22.4px]">
            다운로드
          </NvButton>
          {/* Figma 546:708 → ver.7 847:44944: 84x40, 글자 뒤에 Lucide x 16px 닫기 아이콘 — 폭이 모자라면 오른쪽 묶음 전체가 밀려요 */}
          <NvButton variant="secondary" size="sm" onClick={close} className="h-10 gap-2 px-[17px] text-[14px] font-medium leading-[22.4px]">
            닫기
            <NvLucideCloseIcon size={16} />
          </NvButton>
        </div>
      </header>

      <div className="flex-1 bg-nv-bg-page">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-5 pb-16 pt-7 lg:flex-row lg:items-start xl:px-[120px]">
          <nav aria-label="문서 목차" className="flex w-full shrink-0 flex-col gap-1 rounded-nv-card border border-nv-border bg-nv-surface px-[18px] py-5 lg:w-[260px]">
            <span className="text-[13px] font-bold leading-4 text-nv-text-primary">문서 목차</span>
            {def.toc.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setPage(i)}
                aria-current={page === i ? "page" : undefined}
                className={cn("text-left text-[13px] leading-4", page === i ? "font-bold text-nv-brand-text" : "text-nv-text-secondary hover:text-nv-text-primary")}
              >
                {item.title}
              </button>
            ))}
          </nav>
          <div className="flex min-w-0 flex-1 flex-col items-center gap-[14px]">
            <div className="flex items-center gap-4 rounded-nv-pill border border-nv-border bg-nv-surface px-4 py-[6px] font-num text-[13px] leading-4 text-nv-text-primary">
              <span className="flex items-center gap-2">
                <button type="button" aria-label="이전 페이지" disabled={page === 0} onClick={() => setPage(page - 1)} className="disabled:opacity-30">
                  ‹
                </button>
                <span>
                  {page + 1} / {pages.length}
                </span>
                <button type="button" aria-label="다음 페이지" disabled={page >= pages.length - 1} onClick={() => setPage(page + 1)} className="disabled:opacity-30">
                  ›
                </button>
              </span>
              <span className="flex items-center gap-2">
                <button type="button" aria-label="축소" disabled={zoom === ZOOM_STEPS[0]} onClick={() => zoomBy(-1)} className="disabled:opacity-30">
                  −
                </button>
                <span>{zoom}%</span>
                <button type="button" aria-label="확대" disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]} onClick={() => zoomBy(1)} className="disabled:opacity-30">
                  +
                </button>
              </span>
            </div>
            <div className="w-full overflow-x-auto">
              <div
                className={cn("mx-auto w-[820px] max-w-none rounded-nv-card bg-nv-surface px-14 pb-14 pt-12", DOC_CLASS)}
                style={{ zoom: zoom / 100 }}
                // 서버(agent_definition_service)가 값마다 이스케이프해 만든 HTML이에요
                dangerouslySetInnerHTML={{ __html: pages[page]?.html ?? "" }}
              />
            </div>
          </div>
        </div>
      </div>
      {dialogs}
    </div>
  );
}
